import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ClipboardDocumentIcon,
  CheckIcon,
  LinkIcon,
  ShareIcon,
} from "@heroicons/react/20/solid";
import { QRCodeSVG } from "qrcode.react";
import { useToast } from "../../hooks/useToast";
import { getBookingUrl, getBookingUrlPrefix } from "../../utility/environment";

const BookingPageCard = ({ bookingPageData, isLoading }) => {
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  if (isLoading) {
    return (
      <div className="bg-white rounded-card p-4 shadow-hairline border border-ink-100">
        <div className="animate-pulse space-y-3">
          <div className="h-4 w-24 bg-cream-300 rounded"></div>
          <div className="h-4 w-48 bg-cream-300 rounded"></div>
        </div>
      </div>
    );
  }

  const slug = bookingPageData?.slug;
  const isActive = bookingPageData?.isActive;
  const bookingUrl = getBookingUrl(slug);

  const copyLink = async () => {
    if (!bookingUrl) return;
    try {
      await navigator.clipboard.writeText(bookingUrl);
      setCopied(true);
      toast.success("Link Copied", "Booking link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy Failed", "Could not copy link to clipboard");
    }
  };

  const shareLink = async () => {
    if (!bookingUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Book an appointment",
          url: bookingUrl,
        });
      } catch {
        // User cancelled share dialog — not an error
      }
    } else {
      copyLink();
    }
  };

  // No booking page configured — should not happen post-onboarding
  if (!slug) {
    return null;
  }

  return (
    <div className={`rounded-card p-4 shadow-hairline ${isActive ? "bg-white border border-ink-100" : "bg-signal-amber-50 border border-signal-amber-100"}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-ink-900">Booking Page</h3>
        <div className="flex items-center gap-x-1.5">
          <span
            className={`inline-block size-2 rounded-full ${
              isActive ? "bg-signal-green-500" : "bg-ink-300"
            }`}
          ></span>
          <span
            className={`text-xs font-medium ${
              isActive ? "text-accent-700" : "text-ink-500"
            }`}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        </div>
      </div>

      {/* Inactive warning */}
      {!isActive && slug && (
        <div className="bg-signal-amber-50 border border-signal-amber-100 rounded-md p-2 mb-3">
          <p className="text-xs text-signal-amber-600">
            Your booking page is inactive. Clients cannot book.
          </p>
          <button
            onClick={() => navigate("/booking-page")}
            className="text-xs font-medium text-signal-amber-600 hover:text-amber-900 underline mt-1"
          >
            Activate
          </button>
        </div>
      )}

      {/* Booking URL */}
      <div className="flex items-center gap-x-2 bg-white rounded-md border border-ink-100 px-3 py-2 mb-3">
        <LinkIcon className="size-3.5 text-ink-400 flex-shrink-0" />
        <span className="text-xs text-ink-600 truncate flex-1">
          {getBookingUrlPrefix().replace(/^https?:\/\//, "")}
          {slug}
        </span>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={copyLink}
          className="flex-1 inline-flex items-center justify-center gap-x-1.5 rounded-md bg-white px-2.5 py-1.5 text-xs font-medium text-ink-700 ring-1 ring-inset ring-ink-200 hover:bg-cream-100 transition-colors"
        >
          {copied ? (
            <>
              <CheckIcon className="size-3.5 text-signal-green-500" />
              Copied
            </>
          ) : (
            <>
              <ClipboardDocumentIcon className="size-3.5" />
              Copy Link
            </>
          )}
        </button>
        <button
          onClick={shareLink}
          className="flex-1 inline-flex items-center justify-center gap-x-1.5 rounded-md bg-white px-2.5 py-1.5 text-xs font-medium text-ink-700 ring-1 ring-inset ring-ink-200 hover:bg-cream-100 transition-colors"
        >
          <ShareIcon className="size-3.5" />
          Share
        </button>
      </div>

      {/* QR Code toggle */}
      <button
        onClick={() => setShowQR(!showQR)}
        className="w-full text-xs text-ink-500 hover:text-ink-700 mt-3 text-center"
      >
        {showQR ? "Hide QR Code" : "Show QR Code"}
      </button>

      {showQR && bookingUrl && (
        <div className="flex justify-center mt-3 p-3 bg-white border border-ink-100 rounded-lg">
          <QRCodeSVG value={bookingUrl} size={96} level="M" />
        </div>
      )}
    </div>
  );
};

export default BookingPageCard;
